import React from 'react'
import classnames from 'classnames'
import Image from 'next/image'

import Obituary from '../models/Obituary'
import { Case, LifePageNavItem, ObituarySnippet } from '../../types'
import styles from './styles.module.scss'
import Link from 'next/link'
import { routes } from '../../routes'

interface Props {
  activeNavItem: LifePageNavItem
  obituary: ObituarySnippet | Case
}

const LifePageNavigation: React.FC<Props> = ({ activeNavItem, obituary }) => {
  const { deceased, enable_flowerstore } = obituary
  const navItemClass = React.useCallback(
    (item: LifePageNavItem): string =>
      classnames(styles.navItem, {
        [styles.navItemActive]: activeNavItem === item,
      }),
    [activeNavItem],
  )

  const mobileNavItems = (
    <>
      <li>
        <Link href={routes.obituary(obituary)}>
          <a className={navItemClass(LifePageNavItem.OBITUARY)}>Obituary</a>
        </Link>
      </li>

      <li>
        <Link href={routes.memories(obituary)}>
          <a className={navItemClass(LifePageNavItem.MEMORIES)}>Memories</a>
        </Link>
      </li>

      <li>
        <Link href={routes.services(obituary)}>
          <a className={navItemClass(LifePageNavItem.SERVICES)}>Services</a>
        </Link>
      </li>
    </>
  )

  const desktopNavItems = (
    <>
      {mobileNavItems}

      {enable_flowerstore && (
        <li>
          <Link href={routes.flowerstore(obituary)}>
            <a className={navItemClass(LifePageNavItem.FLOWERS)}>Send Flowers</a>
          </Link>
        </li>
      )}

      {enable_flowerstore && (
        <li>
          <Link href={routes.memorialTrees(obituary)}>
            <a className={navItemClass(LifePageNavItem.MEMORIAL_TREES)}>Memorial Trees</a>
          </Link>
        </li>
      )}
    </>
  )

  const deceasedDates = (
    <p className={styles.deceasedDate}>
      {deceased.date_of_birth && (
        <>
          <span className={styles.deceasedDOB}>{deceased.date_of_birth} - </span>{' '}
        </>
      )}
      {deceased.death_occurred_at}
    </p>
  )

  return (
    <>
      <div className={classnames(styles.mobileWrapper, 'print-hide')}>
        <div className={styles.imageWrapper}>
          <Image
            alt={Obituary.deceasedFullName(obituary, { includeMiddleName: true })}
            className={styles.image}
            layout="fill"
            objectFit="cover"
            src={deceased.image || '/default-obit.jpg'}
            unoptimized
          />
        </div>

        <div>
          <h3 className={styles.deceasedName}>
            {Obituary.deceasedFullName(obituary, { includeMiddleName: true })}
          </h3>

          {deceasedDates}
        </div>
      </div>

      <nav className={classnames(styles.nav, 'print-hide')}>
        <ul className={styles.navList}>{mobileNavItems}</ul>
      </nav>

      {enable_flowerstore && (
        <div className={classnames(styles.navButtons, 'print-hide')}>
          <Link href={routes.flowerstore(obituary)}>
            <a className={styles.navButton}>Send Flowers</a>
          </Link>

          <Link href={routes.memorialTrees(obituary)}>
            <a className={styles.navButton}>Plant Trees</a>
          </Link>
        </div>
      )}

      <div className={styles.desktopWrapper}>
        <div className={styles.imageWrapper}>
          <Image
            alt={Obituary.deceasedFullName(obituary, { includeMiddleName: true })}
            className={styles.image}
            layout="fill"
            objectFit="cover"
            src={deceased.image || '/default-obit.jpg'}
            unoptimized
          />
        </div>

        <div>
          <h3 className={styles.deceasedName}>
            {Obituary.deceasedFullName(obituary, { includeMiddleName: true })}
          </h3>

          {deceasedDates}

          <hr className={styles.line} />
        </div>

        <nav className={classnames(styles.desktopNav, 'print-hide')}>
          <ul className={styles.desktopNavList}>{desktopNavItems}</ul>
        </nav>
      </div>
    </>
  )
}

export default LifePageNavigation
